import { buildStorageEmulatorV1MediaUploadUrl, usesStorageEmulator } from '@/firebase/storage-routing'
import { kGcsUserFilesPipelineBucketRef } from '../constants'
import { getSignedUploadUrl } from '../fetch/get-upload-url'
import { InitUploadFileResponse, SignedUploadUrlResponse, UserFileUploadTransport } from '../schemas'

export type ActiveUploadTask = {
  cancel: () => void
}

type UploadTasksRef = {
  current: Record<string, ActiveUploadTask>
}

type UploadInitializedUserFileBlobParams = {
  clientTmpId: string
  uploadTarget: InitUploadFileResponse
  blob: Blob
  onProgress: (progress: number) => void
  uploadTasksRef?: UploadTasksRef
}

function requireNativeObjectKey(uploadTarget: InitUploadFileResponse): string {
  if (!uploadTarget.upload_object_key) {
    throw new Error('Native signed upload target is missing upload_object_key')
  }

  if (uploadTarget.storage_path !== uploadTarget.upload_object_key) {
    throw new Error('Native signed upload target storage_path does not match upload_object_key')
  }

  return uploadTarget.upload_object_key
}

function validateSignedUploadCapability(uploadTarget: InitUploadFileResponse, capability: SignedUploadUrlResponse): void {
  if (capability.file_id !== uploadTarget.file_id) {
    throw new Error('Signed upload response file_id does not match upload target')
  }

  if (!capability.upload_url) {
    throw new Error('Signed upload response is missing upload_url')
  }

  if (capability.method !== 'PUT') {
    throw new Error(`Signed upload response method ${capability.method} is not supported`)
  }

  if (!capability.headers || Array.isArray(capability.headers) || typeof capability.headers !== 'object') {
    throw new Error('Signed upload response headers are invalid')
  }

  if (Object.values(capability.headers).some((value) => typeof value !== 'string')) {
    throw new Error('Signed upload response headers must be strings')
  }

  if (capability.upload_object_key !== uploadTarget.upload_object_key) {
    throw new Error('Signed upload response upload_object_key does not match upload target')
  }
}

function uploadWithXhrBlob({
  clientTmpId,
  method,
  uploadUrl,
  headers,
  blob,
  onProgress,
  uploadTasksRef,
  failureLabel,
}: {
  clientTmpId: string
  method: 'POST' | 'PUT'
  uploadUrl: string
  headers: Record<string, string>
  blob: Blob
  onProgress: (progress: number) => void
  uploadTasksRef?: UploadTasksRef
  failureLabel: string
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    const cleanup = () => {
      if (uploadTasksRef) {
        delete uploadTasksRef.current[clientTmpId]
      }
    }

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || event.total <= 0) return
      onProgress((event.loaded / event.total) * 100)
    }

    xhr.onload = () => {
      cleanup()
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
        return
      }
      reject(new Error(`${failureLabel} failed with status ${xhr.status}`))
    }

    xhr.onerror = () => {
      cleanup()
      reject(new Error(`${failureLabel} failed`))
    }

    xhr.onabort = () => {
      cleanup()
      reject(new Error(`${failureLabel} was cancelled`))
    }

    xhr.open(method, uploadUrl)
    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })

    if (uploadTasksRef) {
      uploadTasksRef.current[clientTmpId] = { cancel: () => xhr.abort() }
    }

    xhr.send(blob)
  })
}

function uploadWithSignedPut(params: {
  clientTmpId: string
  uploadUrl: string
  headers: Record<string, string>
  blob: Blob
  onProgress: (progress: number) => void
  uploadTasksRef?: UploadTasksRef
}): Promise<void> {
  return uploadWithXhrBlob({
    ...params,
    method: 'PUT',
    failureLabel: 'Signed upload',
  })
}

function uploadWithStorageEmulatorMediaPost(params: {
  clientTmpId: string
  objectKey: string
  contentType: string
  blob: Blob
  onProgress: (progress: number) => void
  uploadTasksRef?: UploadTasksRef
}): Promise<void> {
  return uploadWithXhrBlob({
    clientTmpId: params.clientTmpId,
    method: 'POST',
    uploadUrl: buildStorageEmulatorV1MediaUploadUrl(kGcsUserFilesPipelineBucketRef, params.objectKey),
    headers: {
      'Content-Type': params.contentType,
    },
    blob: params.blob,
    onProgress: params.onProgress,
    uploadTasksRef: params.uploadTasksRef,
    failureLabel: 'Storage emulator upload',
  })
}

async function uploadWithNativeGcsSigned(params: UploadInitializedUserFileBlobParams): Promise<void> {
  const objectKey = requireNativeObjectKey(params.uploadTarget)
  const contentType = params.blob.type || 'application/octet-stream'

  // The local Storage emulator cannot accept production signed PUTs, so native
  // local uploads use the emulator media endpoint after init-upload authorizes
  // the file and chooses the object key.
  if (usesStorageEmulator({ bucketUrl: kGcsUserFilesPipelineBucketRef })) {
    await uploadWithStorageEmulatorMediaPost({
      clientTmpId: params.clientTmpId,
      objectKey,
      contentType,
      blob: params.blob,
      onProgress: params.onProgress,
      uploadTasksRef: params.uploadTasksRef,
    })
    return
  }

  const capability = await getSignedUploadUrl({
    fileId: params.uploadTarget.file_id,
    contentType,
  })
  validateSignedUploadCapability(params.uploadTarget, capability)

  await uploadWithSignedPut({
    clientTmpId: params.clientTmpId,
    uploadUrl: capability.upload_url,
    headers: capability.headers,
    blob: params.blob,
    onProgress: params.onProgress,
    uploadTasksRef: params.uploadTasksRef,
  })
}

export async function uploadInitializedUserFileBlob(params: UploadInitializedUserFileBlobParams): Promise<void> {
  switch (params.uploadTarget.upload_transport) {
    case UserFileUploadTransport.NativeGcsSigned:
      return uploadWithNativeGcsSigned(params)
    default:
      throw new Error(`Unsupported upload transport: ${params.uploadTarget.upload_transport as string}`)
  }
}
